import { OutlinedTextFieldProps, TextField as MuiTextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import classnames from 'classnames'
import React, { useMemo } from 'react'
import { useController, UseControllerOptions, useFormContext } from 'react-hook-form'

import { formFieldLabelText } from '../../lib/cart/utils'

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: 'Gotham',

    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#FFFFFF',
      fontFamily: 'Gotham',
      '& fieldset': {
        borderWidth: '1px',
        borderColor: '#000000',
        top: 0,
      },
      '&:hover fieldset': {
        borderColor: '#000000',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '& input, & textarea': {
        padding: '14px 12px',
        fontFamily: 'Gotham',
      },
      '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
      },
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(0, -1.5px) scale(1)',
      position: 'relative',
      fontFamily: 'Gotham',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '120%',
      letterSpacing: '0%',
      marginBottom: '4px',
      pointerEvents: 'none',
    },
    '& .MuiFormLabel-root': {
      position: 'relative',
      fontFamily: 'Gotham',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Gotham',
    },
  },
}))

export interface TextFieldProps
  extends Omit<OutlinedTextFieldProps, 'error' | 'inputRef' | 'onChange' | 'value' | 'variant'> {
  fsExclude?: boolean
  inputMode?: Exclude<OutlinedTextFieldProps['inputProps'], undefined>['inputMode']
  label: string
  name: string
  rules?: UseControllerOptions['rules']
  step?: React.InputHTMLAttributes<unknown>['step']
}

const TextField: React.FC<TextFieldProps> = ({
  className,
  defaultValue = '',
  disabled = false,
  fsExclude = false,
  fullWidth = true,
  id,
  inputMode,
  inputProps = {},
  InputProps = {},
  label,
  name,
  onBlur,
  rules,
  type = 'text',
  InputLabelProps = {},
  ...other
}) => {
  const { control, errors } = useFormContext()
  const { field } = useController({ control, defaultValue, name, rules })
  const classes = useStyles()

  const combinedOnBlur = useMemo(
    (): React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> =>
      onBlur
        ? (e) => {
            // call event handler prior to react-hook-form's event handler in case handler
            // changes the field value, affecting validation
            onBlur(e)
            field.onBlur()
          }
        : field.onBlur,
    [field, onBlur],
  )

  return (
    <MuiTextField
      disabled={disabled}
      className={classnames(classes.root, className, { 'fs-exclude': fsExclude })}
      fullWidth={fullWidth}
      id={id || name}
      inputProps={{ inputMode, ...inputProps }}
      InputLabelProps={{
        ...InputLabelProps,
        // workaround bug with label not shrinking after calling `setValue()`
        shrink: true,
      }}
      InputProps={{
        ...InputProps,
        type,
      }}
      label={formFieldLabelText({ error: errors[name], label })}
      name={name}
      {...other}
      error={!!errors[name]}
      inputRef={field.ref}
      onBlur={combinedOnBlur}
      onChange={field.onChange}
      value={field.value}
      {...(rules?.required === true && { required: true })}
      variant="outlined"
    />
  )
}

export default TextField
